import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe défini ! Vous pouvez vous connecter.");
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1E4D] flex items-center justify-center px-5 hero-grid">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-8" data-testid="reset-password-card">
          <Logo flagClass="h-9" textClass="text-2xl" />

          {!token ? (
            <div className="mt-6 flex items-start gap-3 rounded-lg bg-rose-50 border border-rose-200 p-4" data-testid="reset-no-token">
              <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
              <p className="text-sm text-rose-800">Lien invalide. Veuillez recommencer la procédure depuis la page de connexion.</p>
            </div>
          ) : (
            <>
              <h1 className="font-display mt-6 text-2xl font-bold text-slate-900">Définir votre mot de passe</h1>
              <p className="text-sm text-slate-500 mt-1">Choisissez un mot de passe sécurisé (8 caractères minimum).</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-slate-700 font-medium">Nouveau mot de passe</Label>
                  <Input className="mt-1.5" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" required />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Confirmer le mot de passe</Label>
                  <Input className="mt-1.5" type="password" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" required />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 font-semibold" data-testid="reset-submit-button">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Enregistrer le mot de passe</>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
